import * as React from "react";
import { connect  } from "react-redux";
import { bindActionCreators,Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { liftVersionHasChildren } from "../../utils/pdApiUtils";
import { EditActions, editActions } from "../../actions/editActions";

type StateProps = {
  editedVersion: Api.KceVersion;
  storedVersion: Api.KceVersion;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
  ioConfigurations: Api.IoConfiguration[] | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

const kceVersionFields = nameofFactory<Api.KceVersion>();

export type EditKCEVersionProps = StateProps & DispatchProps & OwnProps;

export class EditKCEVersion extends React.Component<EditKCEVersionProps> {
  render(): JSX.Element {
    const { editedVersion, storedVersion, activeUser, toggleModal, pdApi, ioConfigurations } = this.props;
    const isNew = !storedVersion;
    const { liftParameters, faults } = pdApi;
    const deleteDisabled =  storedVersion && liftVersionHasChildren(storedVersion.id, liftParameters, ioConfigurations || [], faults);
    
    return (
      <Editor
        itemName={"KCE version"}
        originalObject={storedVersion}
        editedObject={editedVersion}
        hasWritePermission={hasWriteAccess(activeUser, "Lift")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("kceVersions")}
        create={() => this.props.editActions.add("kceVersions")}
        remove={{ callback: toggleModal
          , enabledTooltip: "version"
          , disabled: deleteDisabled
          , disabledTooltip: ": Item linked to child components" }}
      >
        <TextField
          id="code"
          label="Number"
          value={editedVersion.number}
          isError={!this.isFieldValid(kceVersionFields("number"))}
          errorMessage={!this.isEmptyString(editedVersion.number) ?"The number should be unique and adhere to the numeric format: 00.00.00.00" : ""}
          readonly={!isNew}
          onChange={code => this.props.editActions.setEdited({ ...editedVersion, number: code.trim() })}
        />
        <TextField
          id="name"
          label="Name"
          value={editedVersion.name}
          isError={!this.isFieldValid(kceVersionFields("name"))}
          errorMessage={!this.isEmptyString(editedVersion.name) ?"The name must be unique." : ""}
          readonly={!isNew}
          onChange={name => this.props.editActions.setEdited({ ...editedVersion, name: name.trim() })}
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedVersion.comment}
          isError={!this.isFieldValid(kceVersionFields("comment"))}
          onChange={comment => this.props.editActions.setEdited({ ...editedVersion, comment: comment.trim() })}
          readonly={!hasWriteAccess(activeUser, "Lift")}
        />
      </Editor>
    );
  }

  private isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);
  
  private isFieldValid = (field: string): boolean => {
    const { editedVersion, pdApi, storedVersion } = this.props;
    const isNew = !storedVersion;
    switch (field) {
      case kceVersionFields("name"): {
        const { name } = editedVersion;
        if (!name || name.replace(/\s/g, "").length === 0) {
          return false;
        } else {
          return _.values(pdApi.kceVersions).find(
            version => (isNew || version.id !== editedVersion.id) && version.name.trim() === name.trim()
          )
            ? false
            : true;
        }
      }
      case kceVersionFields("number"):
        if (!(!!editedVersion.number && editedVersion.number.replace(/\s/g, "").length > 0)) {
          return false;
        } else if (!new RegExp("^\\d{2}\\.\\d{2}\\.\\d{2}\\.\\d{2}$").test(editedVersion.number)) {
          return false;
        } else {
          return _.values(pdApi.kceVersions).find(
            version => version.id !== editedVersion.id && version.number.trim() === editedVersion.number.trim()
          )
              ? false
              : true;
        }
      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedVersion } = this.props;
    const temp = _.keys(editedVersion);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedVersion: state.editView.editedObject,
  storedVersion: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  ioConfigurations: state.ioConfig.ioConfigurations
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditKCEVersion);
